import React, { useState, useEffect, useRef } from 'react';
import { 
  Bell, 
  X, 
  Check, 
  AlertCircle, 
  CheckCircle, 
  Info, 
  AlertTriangle,
  Trash2,
  Settings,
  Eye,
  Clock
} from 'lucide-react';
import toast from 'react-hot-toast';
import OTPActionModal from './OTPActionModal';
import notificationService, { addNotification } from '../services/notificationService';
import notificationApiService from '../services/notificationApiService';

const NotificationCenter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [hasNewOTP, setHasNewOTP] = useState(false);
  const dropdownRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const lastNotificationCountRef = useRef(0);
  const audioContextRef = useRef(null);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Load notifications from service on mount
  useEffect(() => {
    const loadNotifications = async () => {
      await notificationService.loadNotifications();
      const existingNotifications = notificationService.getNotifications();
      setNotifications(existingNotifications);
      lastNotificationCountRef.current = existingNotifications.length;
    };
    
    loadNotifications();
  }, []);

  // Polling mechanism for real-time notifications
  useEffect(() => {
    const pollNotifications = async () => {
      try {
        await notificationService.loadNotifications();
        const updatedNotifications = notificationService.getNotifications();
        
        // Check if there are new notifications
        const currentCount = updatedNotifications.length;
        const previousCount = lastNotificationCountRef.current;
        
        if (currentCount > previousCount) {
          const newNotificationsCount = currentCount - previousCount;
          
          // Play sound for new notifications
          playNotificationSound();
          
          // Show visual feedback
          setHasNewOTP(true);
          setTimeout(() => setHasNewOTP(false), 10000);
          
          // Show toast notification
          toast.success(`${newNotificationsCount} new notification${newNotificationsCount > 1 ? 's' : ''}!`, {
            icon: '🔔',
            duration: 3000
          });
        }
        
        // Only update state if notifications actually changed
        setNotifications(prev => {
          // Compare by ID and length to avoid unnecessary re-renders
          if (prev.length !== updatedNotifications.length) {
            return updatedNotifications;
          }
          
          // Check if any notification changed
          const hasChanged = prev.some((prevNotif, index) => {
            const updatedNotif = updatedNotifications[index];
            return !updatedNotif || 
                   prevNotif.id !== updatedNotif.id || 
                   prevNotif.read !== updatedNotif.read ||
                   prevNotif.status !== updatedNotif.status;
          });
          
          return hasChanged ? updatedNotifications : prev;
        });
        
        lastNotificationCountRef.current = currentCount;
      } catch (error) {
        console.error('Error polling notifications:', error);
      }
    };

    // Poll every 5 seconds
    pollingIntervalRef.current = setInterval(pollNotifications, 5000);

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Note: Notifications are now stored in the database
  // localStorage sync removed to prevent unnecessary re-renders

  // Listen for notifications
  useEffect(() => {
    const unsubscribe = notificationService.subscribe((notification) => {
      setNotifications(prev => {
        const updated = [notification, ...prev].slice(0, 50);
        return updated;
      });

      // Auto-open notification center for OTP notifications
      if (notification.metadata?.isOTP) {
        setHasNewOTP(true);
        setIsOpen(true);
        
        // Clear the new OTP indicator after 10 seconds
        setTimeout(() => {
          setHasNewOTP(false);
        }, 10000);
      }
    });

    return unsubscribe;
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  // Get status badge for notifications
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800', icon: X }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={10} className="mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Play notification sound
  const playNotificationSound = async () => {
    try {
      
      // Method 1: Try Web Audio API
      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }

        const audioContext = audioContextRef.current;
        
        // Resume audio context if suspended (required by browsers)
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
          console.log('🔊 Audio context resumed');
        }

        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        // Create a more noticeable notification sound (three tones)
        oscillator.type = 'sine'; // sine wave for pleasant sound
        oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(900, audioContext.currentTime + 0.2);
        
        gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.4);

        console.log('✅ Web Audio API sound played successfully');
      } catch (webAudioError) {
        console.error('❌ Web Audio API failed:', webAudioError);
        
        // Method 2: Fallback to simple beep
        const audioElement = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjaL0fPTgjMGHm7A7+OZUA0PVqzn77BdGAg+ltryxnMpBSuAy/LZjTwIGGS37OKdUg4KUKXh8K5kHgU2jM/xzH4sAyJ1xO3akD8JFF2x5+yoWhYKRp7d8LxuIQU1idDyznwqBSF0wO7PnEwODFWq5O+wXRgHPpTX8sF0JwQ2jtHxyn8rBSJ1xO3akD8JFF2x5+yoWhYKRp7d8LxuIQU1idDyznwqBSF0wO7PnEwODFWq5O+wXRgHPpTX8sF0JwQ2jtHxyn8rBSJ1xO3akD8JFF2x5+yoWhYKRp7d8LxuIQU1idDyznwqBSF0wO7PnEwODFWq5O+wXRgHPpTX8sF0JwQ2jtHxyn8rBSJ1xO3akD8JFF2x5+yoWhYKRp7d8LxuIQU1idDyznwqBSF0wO7PnEwODFWq5O+wXRgHPpTX8sF0JwQ2jtHxyn8rBSJ1xO3akD8JFF2x5+yoWhYKRp7d8LxuIQU1idDyznwqBSF0wO7PnEwODFWq5O+wXRgHPpTX8sF0JwQ2jtHxyn8rBSJ1xO3akD8JFF2x5+yoWhYKRp7d8LxuIQU1idDyznwqBSF0wO7PnEwODFWq5O+wXRgHPpTX8sF0JwQ2jtHxyn8rBSJ1xO3akD8JFF2x5+yoWhYKRp7d8LxuIQU1idDyznwqBSF0wO7PnEwODFWq5O+wXRgHPpTX8sF0JwQ2jtHxyn8rBSJ1xO3akD8JFF2x5+yoWhYKRp7d8LxuIQU1idDyznwqBSF0wO7PnEwODFWq5O+wXRgHPpTX8sF0JwQ2jtHxyn8rBSJ1xO3akD8JFF2x5+yoWhYKRp7d8LxuIQU1idDyznwqBSF0wO7PnEwODFWq5O+wXRgHPpTX8sF0JwQ2jtHxyn8rBSJ1xO3akD8JFF2x5+yoWhYKRp7d8LxuIQU1idDyznwqBSF0wO7PnEwODFWq5O+wXRgHPpTX8sF0JwQ=');
        audioElement.volume = 0.5;
        await audioElement.play();
        console.log('✅ Fallback audio played successfully');
      }
    } catch (error) {
      console.error('❌ All audio methods failed:', error);
      // If audio fails, at least show a visual notification
      console.log('💡 Audio blocked by browser. User needs to interact with page first.');
    }
  };
  
  // Debug: Log notification state
  useEffect(() => {
    console.log('🔔 NotificationCenter state update:', {
      notificationsCount: notifications.length,
      unreadCount,
      isOpen,
      hasNewOTP
    });
  }, [notifications, unreadCount, isOpen, hasNewOTP]);
  

  const markAsRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationApiService.markAllAsRead();
      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true }))
      );
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const deleteNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAll = async () => {
    try {
      await notificationService.clearAll();
      setNotifications([]);
      toast.success('All notifications cleared');
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'error':
        return <AlertCircle className="text-red-500" size={20} />;
      case 'warning':
        return <AlertTriangle className="text-yellow-500" size={20} />;
      default:
        return <Info className="text-blue-500" size={20} />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'success':
        return 'border-l-green-500 bg-green-50/50';
      case 'error':
        return 'border-l-red-500 bg-red-50/50';
      case 'warning':
        return 'border-l-yellow-500 bg-yellow-50/50';
      default:
        return 'border-l-blue-500 bg-blue-50/50';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Just now';
    
    const date = new Date(timestamp);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Just now';
    }
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={async () => {
          console.log('🔔 Bell clicked. Current state:', { isOpen, unreadCount, hasNewOTP });
          
          // Initialize audio context on user interaction (required by browsers)
          if (!audioContextRef.current) {
            try {
              audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
              await audioContextRef.current.resume();
              console.log('🔊 Audio context initialized on user interaction');
            } catch (error) {
              console.error('Failed to initialize audio context:', error);
            }
          }
          
          setIsOpen(!isOpen);
          setHasNewOTP(false);
        }}
        className={`relative p-2 rounded-lg text-white/80 hover:bg-white/20 hover:text-white transition-all ${
          hasNewOTP ? 'animate-bounce' : ''
        }`}
        aria-label="Notifications"
      >
        <Bell size={20} className={hasNewOTP ? 'animate-pulse' : ''} />
        {unreadCount > 0 && (
          <span className={`absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ${
            hasNewOTP ? 'animate-ping' : 'animate-pulse'
          }`}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Bell size={20} />
              <h3 className="font-semibold text-lg">Notifications</h3>
              {unreadCount > 0 && (
                <span className="bg-white/20 text-white text-xs font-bold rounded-full px-2 py-0.5">
                  {unreadCount} new
                </span>
              )}
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded hover:bg-white/20 transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {/* Actions Bar */}
          <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-b border-gray-200">
            <div className="flex items-center space-x-3">
              {notifications.length > 0 && (
                <>
                  <button
                    onClick={markAllAsRead}
                    disabled={unreadCount === 0}
                    className={`text-sm font-medium transition-colors ${
                      unreadCount === 0
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-blue-600 hover:text-blue-800'
                    }`}
                  >
                    <div className="flex items-center space-x-1">
                      <Check size={14} />
                      <span>Mark all read</span>
                    </div>
                  </button>
                  <button
                    onClick={clearAll}
                    className="text-sm font-medium text-red-600 hover:text-red-800 transition-colors"
                  >
                    <div className="flex items-center space-x-1">
                      <Trash2 size={14} />
                      <span>Clear all</span>
                    </div>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-[400px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">No notifications</p>
                <p className="text-gray-400 text-sm mt-1">You're all caught up!</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`px-4 py-3 hover:bg-gray-50 transition-colors border-l-4 ${
                      getTypeColor(notification.type)
                    } ${!notification.read ? 'bg-blue-50/30' : ''}`}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-0.5">
                        {getIcon(notification.type)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <p className={`text-sm font-semibold ${
                                notification.read ? 'text-gray-700' : 'text-gray-900'
                              }`}>
                                {notification.title}
                              </p>
                              {notification.status && (
                                <div className="ml-2">
                                  {getStatusBadge(notification.status)}
                                </div>
                              )}
                            </div>
                            <p className={`text-sm mt-1 ${
                              notification.read ? 'text-gray-500' : 'text-gray-700'
                            }`}>
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {formatTimestamp(notification.timestamp)}
                            </p>
                            
                            {/* View Button for OTP notifications */}
                            {notification.metadata?.isOTP && (
                              <button
                                onClick={() => {
                                  setSelectedNotification(notification);
                                  setShowOTPModal(true);
                                  markAsRead(notification.id);
                                }}
                                className="mt-2 inline-flex items-center space-x-1 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors"
                              >
                                <Eye size={14} />
                                <span>View & Verify</span>
                              </button>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center space-x-1 ml-2">
                            {!notification.read && (
                              <button
                                onClick={() => markAsRead(notification.id)}
                                className="p-1 rounded hover:bg-white transition-colors"
                                title="Mark as read"
                              >
                                <Check size={14} className="text-blue-600" />
                              </button>
                            )}
                            <button
                              onClick={() => deleteNotification(notification.id)}
                              className="p-1 rounded hover:bg-white transition-colors"
                              title="Delete"
                            >
                              <X size={14} className="text-gray-400 hover:text-red-600" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
              <button
                className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                onClick={() => {
                  // Navigate to notifications page if exists
                  setIsOpen(false);
                  toast('Full notification history coming soon!', { icon: '🔔' });
                }}
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}

      {/* OTP Action Modal */}
      <OTPActionModal
        isOpen={showOTPModal}
        onClose={async () => {
          setShowOTPModal(false);
          setSelectedNotification(null);
          // Reload notifications to show updated status
          await notificationService.loadNotifications();
          const updatedNotifications = notificationService.getNotifications();
          setNotifications(updatedNotifications);
        }}
        notificationData={selectedNotification}
      />
    </div>
  );
};


export default NotificationCenter;

